import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react';
import { CATEGORY_INFO, TOP_LEVEL_CATEGORIES } from '../data/categoryInfo';
import { useSeo } from '../hooks/useSeo';

/** Pagina istituzionale di dettaglio per una categoria merceologica,
 * mostrata a TUTTI gli utenti (loggati e non) su `/i-nostri-prodotti/:categorySlug`.
 * Contenuto e struttura SEO ripresi dal documento "DTK_Contenuti_Web_SEO_v1"
 * fornito dall'utente. Il catalogo/ricerca prodotti reale vive invece su
 * `/prodotti/:categoryId` (ProductSearchPage), indirizzo separato su
 * richiesta utente (15/02/2026) per rendere la ricerca fruibile anche ai
 * non loggati. */
export default function CategoryInfoPage(): React.ReactElement | null {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const category = CATEGORY_INFO.find(c => c.slug === categorySlug);
  useSeo(category ? category.seoTitle : 'Prodotti | DTK', category?.metaDescription);
  if (!category) return <Navigate to="/i-nostri-prodotti" replace />;

  const activeTopSlug = category.parentSlug ?? category.slug;

  return (
    <div data-testid="category-info-page">
      <div className="border-b bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <h1 className="text-lg font-semibold" data-testid="category-info-title">{category.cardTitle}</h1>
          <Link
            to="/i-nostri-prodotti"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="category-info-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Torna indietro
          </Link>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row gap-8">
        {/* Sidebar categorie di primo livello */}
        <nav className="w-full sm:w-52 flex-shrink-0" data-testid="category-info-sidebar">
          <ul className="flex sm:flex-col gap-1 flex-wrap">
            {TOP_LEVEL_CATEGORIES.map(c => (
              <li key={c.slug}>
                <Link
                  to={`/i-nostri-prodotti/${c.slug}`}
                  data-testid={`category-info-nav-${c.slug}`}
                  className={`block px-2 py-1.5 text-sm rounded-md transition-colors ${
                    c.slug === activeTopSlug
                      ? 'text-[#2B5EA7] font-semibold bg-[#EBF0F7]'
                      : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
                  }`}
                >
                  {c.navLabel.toUpperCase()}
                </Link>
                {/* Sotto-categorie di "Gruppi completi" */}
                {c.slug === 'gruppi-completi' && activeTopSlug === 'gruppi-completi' && (
                  <ul className="ml-3 mt-1 mb-1 space-y-0.5 border-l pl-3">
                    {c.subcategories?.map(sub => (
                      <li key={sub.slug}>
                        <Link
                          to={`/i-nostri-prodotti/${sub.slug}`}
                          data-testid={`category-info-subnav-${sub.slug}`}
                          className={`block px-2 py-1 text-xs rounded-md transition-colors ${
                            sub.slug === category.slug
                              ? 'text-[#2B5EA7] font-semibold bg-[#EBF0F7]'
                              : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
                          }`}
                        >
                          {sub.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* Contenuto */}
        <div className="flex-1 flex flex-col sm:flex-row gap-8" data-testid="category-info-content">
          <div className="flex-1 max-w-2xl">
            {category.parentSlug && (
              <Link
                to={`/i-nostri-prodotti/${category.parentSlug}`}
                className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-3 transition-colors"
                data-testid="category-info-parent-link"
              >
                <ArrowLeft className="w-3 h-3" /> Torna a Gruppi completi
              </Link>
            )}
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight mb-6">{category.heading}</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">{category.intro}</p>

            {category.sections.map((s, i) => (
              <div key={i} className="mb-6">
                <h3 className="text-base font-semibold mb-3">{s.h2}</h3>
                {s.body && <p className="text-sm text-muted-foreground leading-relaxed mb-2">{s.body}</p>}
                {s.bullets && (
                  <ul className="space-y-1.5 mt-2">
                    {s.bullets.map((b, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#2B5EA7] flex-shrink-0 mt-0.5" />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}

            {/* Sottocategorie (solo pagina "Gruppi completi") */}
            {category.subcategories && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-testid="category-info-subcategories">
                {category.subcategories.map(sub => (
                  <Link
                    key={sub.slug}
                    to={`/i-nostri-prodotti/${sub.slug}`}
                    className="group p-4 rounded-md border bg-white hover:border-[#2B5EA7] transition-colors"
                    data-testid={`category-info-subcard-${sub.slug}`}
                  >
                    <h4 className="text-sm font-semibold text-[#2B5EA7] mb-1 group-hover:underline">{sub.label}</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">{sub.description}</p>
                  </Link>
                ))}
              </div>
            )}

            {category.cta && (
              <Link
                to={category.cta.href}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2B5EA7] hover:underline"
                data-testid="category-info-cta-link"
              >
                {category.cta.label} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
          <div className="w-full sm:w-64 flex-shrink-0">
            <div className="rounded-md overflow-hidden aspect-square bg-secondary">
              <img src={category.image} alt={category.cardTitle} className="w-full h-full object-cover" data-testid="category-info-image" />
            </div>
          </div>
        </div>
      </div>

      {/* CTA registrazione */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pb-10">
        <div className="relative overflow-hidden rounded-lg bg-gradient-to-r from-[#1E4D8C] to-[#2B5EA7] p-8 sm:p-10 flex flex-col sm:flex-row items-center gap-6 shadow-lg" data-testid="category-info-cta">
          <div className="flex-1">
            <h2 className="text-lg sm:text-xl font-semibold text-white mb-1.5">Sei un rivenditore?</h2>
            <p className="text-white/80 text-sm">
              Registrati per accedere al catalogo completo con prezzi riservati, disponibilità in tempo reale e ordini online.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <Link
              to="/registrati"
              data-testid="category-info-register"
              className="h-11 px-6 rounded-md bg-gradient-to-r from-amber-300 to-amber-400 text-[#1E4D8C] text-sm font-bold flex items-center gap-2 hover:from-amber-400 hover:to-amber-500 hover:scale-[1.03] shadow-sm hover:shadow-md transition-all"
            >
              Registrati ora
            </Link>
            <Link
              to="/accedi"
              data-testid="category-info-login"
              className="h-11 px-6 rounded-md border-2 border-white/50 text-white text-sm font-medium flex items-center hover:border-amber-400 hover:bg-amber-400 hover:text-[#1E4D8C] transition-colors"
            >
              Accedi
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
